import React, { useState, useEffect } from 'react'
import { Shield, BarChart3, Eye, Settings, X } from 'lucide-react'
import type { WindowWithPostHog } from '../types/posthog'

export type ConsentLevel = 'full' | 'limited' | 'minimal' | 'off'

const CONSENT_KEY = 'nura_consent'

export function getConsent(): ConsentLevel | null {
  try {
    return (localStorage.getItem(CONSENT_KEY) as ConsentLevel) || null
  } catch {
    return null
  }
}

export function setConsent(level: ConsentLevel) {
  try {
    localStorage.setItem(CONSENT_KEY, level)
    window.dispatchEvent(new CustomEvent('nura:consent-changed', { detail: level }))
  } catch {}
}

function isDNTEnabled(): boolean {
  const w = window as WindowWithPostHog
  return (
    navigator.doNotTrack === '1' ||
    w?.msDoNotTrack === '1' ||
    w?.doNotTrack === '1'
  )
}

interface ConsentBannerProps {
  className?: string
  onClose?: () => void
}

export function ConsentBanner({ className, onClose }: ConsentBannerProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [selectedOption, setSelectedOption] = useState<ConsentLevel | null>(null)
  const [dnt, setDnt] = useState(false)

  useEffect(() => {
    const dntEnabled = isDNTEnabled()
    setDnt(dntEnabled)

    const existing = getConsent()
    if (dntEnabled) {
      // Respect DNT: never run Full; force Limited unless explicitly Off
      if (existing !== 'off') setConsent('limited')
      setIsVisible(false)
    } else if (existing == null) {
      // Show after a brief delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleChoice = (choice: ConsentLevel) => {
    setSelectedOption(choice)
    setConsent(choice)

    // Track the choice for analytics (if analytics are enabled)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'privacy_consent', {
        event_category: 'privacy',
        event_label: choice,
      })
    }

    // Close after a brief delay to show selection
    setTimeout(() => {
      setIsVisible(false)
      onClose?.()
    }, 500)
  }

  if (!isVisible) return null

  const options = [
    {
      id: 'off' as const,
      icon: X,
      title: 'No analytics',
      description: 'No data sharing, privacy-first experience',
      buttonClass: 'border-neutral-300 text-neutral-600 hover:bg-neutral-50',
    },
    {
      id: 'minimal' as const,
      icon: Shield,
      title: 'App improvements only',
      description: 'Basic error tracking and feature usage for app stability',
      buttonClass: 'border-coral-200 text-coral-700 hover:bg-coral-50',
    },
    {
      id: 'limited' as const,
      icon: BarChart3,
      title: 'Usage insights',
      description: 'Anonymous usage patterns to enhance your experience',
      buttonClass: 'border-sage-200 text-sage-700 hover:bg-sage-50',
    },
    {
      id: 'full' as const,
      icon: Settings,
      title: 'Full optimization',
      description: 'Complete analytics for personalized recommendations',
      buttonClass: dnt 
        ? 'border-neutral-200 text-neutral-400 cursor-not-allowed opacity-50'
        : 'border-coral-300 bg-coral-500 text-white hover:bg-coral-600',
      disabled: dnt,
      tooltip: dnt ? 'Turn off Do Not Track in your browser to enable' : undefined,
    },
  ]

  return (
    <div className={`fixed bottom-0 inset-x-0 z-50 ${className || ''}`}>
      <div className="bg-white border-t border-neutral-200 shadow-prominent">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 mt-1">
              <div className="w-8 h-8 rounded-full bg-coral-100 flex items-center justify-center">
                <Shield className="w-4 h-4 text-coral-600" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-medium text-neutral-800 mb-1">
                    Help us improve nura
                  </h3>
                  <p className="text-sm text-neutral-600 mb-4">
                    Choose what you share. We never record personal details or recipe content.
                  </p>

                  {/* Options Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {options.map((option) => {
                      const Icon = option.icon
                      const isSelected = selectedOption === option.id
                      
                      return (
                        <button
                          key={option.id}
                          onClick={() => !option.disabled && handleChoice(option.id)}
                          disabled={option.disabled}
                          title={option.tooltip}
                          className={`
                            flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all
                            ${isSelected 
                              ? 'border-coral-400 bg-coral-50 shadow-sm' 
                              : option.buttonClass
                            }
                          `}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm leading-tight">
                              {option.title}
                            </div>
                            <div className="text-xs opacity-80 mt-0.5 leading-tight">
                              {option.description}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => handleChoice('off')}
                  className="flex-shrink-0 p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
                  aria-label="Decline analytics"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <p className="text-xs text-neutral-500 text-center">
              You can change this anytime in Settings → Privacy. 
              {dnt && ' Do Not Track is enabled in your browser.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Export for backwards compatibility
export { ConsentBanner as PrivacyConsent }